/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Section } from 'ui/molecules/Section/Section';
import { SplitRow } from 'ui/molecules/SplitRow/SplitRow';

import styles from './FinancialServicesProblems.module.scss';

type Translate = (key: string) => string;

type Problem = {
  name: string;
  pressure: ReactNode;
  help: ReactNode;
};

const getProblems = (t: Translate): Problem[] => [
  {
    name: t('Defending a decision to a regulator'),
    pressure: t(
      'A supervisor asks how an onboarding, credit or screening decision was reached — and the answer sits inside a vendor’s aggregated score you can’t open.',
    ),
    help: (
      <>
        {t('Every field carries its')} <b>{t('registry, filing link and retrieval timestamp')}</b>
        {t(', so any decision reconstructs from the source document, not a model.')}
      </>
    ),
  },
  {
    name: t('Onboarding counterparties across borders'),
    pressure: t(
      'A counterparty sits in a jurisdiction your provider doesn’t cover, or covers with data months out of date — and onboarding stalls or over-rejects.',
    ),
    help: (
      <>
        {t('Primary records from')} <b>{t('400+ registries across 200+ countries')}</b>
        {t(', synced daily, so cross-border checks resolve instead of hitting a gap.')}
      </>
    ),
  },
  {
    name: t('Resolving who actually controls an entity'),
    pressure: t(
      'Beneficial ownership is buried under holding companies and nominees across borders, while AML obligations require you to reach the people behind it.',
    ),
    help: (
      <>
        {t('A')} <b>{t('traversable ownership graph')}</b>
        {t(
          ', each hop tied to the filing that establishes it, down to the UBO with filed percentages where they exist.',
        )}
      </>
    ),
  },
  {
    name: t('Screening without drowning in false positives'),
    pressure: t(
      'Name-based sanctions and PEP screening floods the review queue with near-matches, and genuine hits get lost in the noise.',
    ),
    help: (
      <>
        {t('Screening the')} <b>{t('registry-resolved entity and its people')}</b>{' '}
        {t('— not raw strings — so a hit is traceable to the exact party and the queue stays focused.')}
      </>
    ),
  },
  {
    name: t('Keeping risk current between reviews'),
    pressure: t(
      'A director changes or a company’s status moves the day after your annual refresh, and you only find out at the next scheduled review.',
    ),
    help: (
      <>
        {t('Continuous monitoring surfaces')} <b>{t('filing, status and ownership changes as the record moves')}</b>
        {t(', each alert carrying the source of the field it updates.')}
      </>
    ),
  },
];

const ProblemRow = ({ problem, index }: { problem: Problem; index: number }) => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.35 });

  return (
    <div ref={ref} className={cn(styles.row, revealed && styles.rowIn)}>
      <span className={styles.ghostIndex} aria-hidden="true">
        {String(index + 1).padStart(2, '0')}
      </span>

      <div className={styles.nameRow}>
        <span className={styles.badge}>{String(index + 1).padStart(2, '0')}</span>
        <h3 className={styles.name}>{problem.name}</h3>
      </div>
      <SplitRow
        align="start"
        gap="lg"
        className={styles.split}
        start={
          <div className={styles.col}>
            <span className={styles.label}>{t('The pressure')}</span>
            <p className={styles.text}>{problem.pressure}</p>
          </div>
        }
        end={
          <div className={cn(styles.col, styles.colHelp)}>
            <span className={cn(styles.label, styles.labelHelp)}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              {t('How we help')}
            </span>
            <p className={styles.text}>{problem.help}</p>
          </div>
        }
      />
    </div>
  );
};

export const FinancialServicesProblems = () => {
  const { t } = useInt();

  return (
    <Section
      title={t('The problems that land on a risk desk.')}
      introBlockClassName={styles.intro}
      background="#F6F7F9"
      className={styles.root}
    >
      <div className={styles.list}>
        {getProblems(t).map((problem, index) => (
          <ProblemRow key={index} problem={problem} index={index} />
        ))}
      </div>
    </Section>
  );
};
